import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormControl, UntypedFormBuilder, Validators} from '@angular/forms';
import {BehaviorSubject, Subject, switchMap} from 'rxjs';
import {Store} from '@ngrx/store';
import {MatLegacySnackBar as MatSnackBar, MatLegacySnackBarConfig as MatSnackBarConfig} from '@angular/material/legacy-snack-bar';
import {MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig} from '@angular/material/legacy-dialog';
import {takeUntil} from 'rxjs/operators';

import {DirectoryOrganization} from '@app/core/model/entity-profile';
import {AuthenticatedUserService} from '@app/core/service/authenticated-user.service';
import {State} from '@app/app.reducers';
import {IntegrationService} from '@app/core/service/integration.service';
import {SEND_SMS_FAILED_STATUS_CODE, User} from '@app/core/model/user';
import {OrgsService} from '@app/core/service/orgs.service';
import {MESSAGES} from '@app/core/model/messages';
import {copyData} from '@app/core/model/integration-models';
import {SmsMfaDialogViews, VerifyPhoneDialogComponent} from '@app/core/comp/dialog/verify-phone-dialog/verify-phone-dialog.component';
import {getDigitsOnly} from '@app/core/utils/other-utils';
import * as authAction from '@app/views/common/auth.actions';

@Component({
    selector: 'ngc-mfa-setup',
    templateUrl: './mfa-setup.component.html',
    styleUrls: ['./mfa-setup.component.scss']
})
export class MFASetupComponent implements OnInit {

    @Input() loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    @Output() backToCredentialsLogIn = new EventEmitter<void>();
    @Output() performCredentialsLogin = new EventEmitter<void>();
    @Output() turnOnCredentialsLoginView = new EventEmitter<void>();

    phoneNumberCtrl = new FormControl({value: '', disabled: true}, [Validators.maxLength(32), AuthenticatedUserService.isValidPhoneNumberValidator]);
    currentOrg: DirectoryOrganization;
    user: User;
    private unsubscribe$ = new Subject<void>();

    constructor(private formBuilder: UntypedFormBuilder,
                public userService: AuthenticatedUserService,
                private store: Store<State>,
                private snackBar: MatSnackBar,
                public dialog: MatDialog,
                public authenticatedUserService: AuthenticatedUserService,
                public integrationService: IntegrationService,
                public orgService: OrgsService) {
    }

    ngOnInit(): void {
        this.authenticatedUserService.getAuthenticatedUser()
            .pipe(
                takeUntil(this.unsubscribe$),
                switchMap((user: User) => {
                        this.user = user;
                        return this.orgService.getOrg(user?.orgGuid).pipe(takeUntil(this.unsubscribe$))
                    }
                )
            )
            .subscribe((currentOrg: DirectoryOrganization) => {
                    this.currentOrg = currentOrg;
                    const prePopulatedValue = this.getPrePopulatedMFAPhoneNumber();
                    this.phoneNumberCtrl.enable();
                    this.phoneNumberCtrl.setValue(prePopulatedValue);

                    this.loading$.next(false);
                },
                error => {
                    this.snackBar.open(MESSAGES.UNKNOWN_ERROR, 'ERROR', <MatSnackBarConfig>{duration: 3000});
                    this.loading$.next(false);
                    this.onBackToCredentialsLogIn();
                });
    }

    onBackToCredentialsLogIn() {
        this.backToCredentialsLogIn.emit();
        this.store.dispatch(new authAction.SignOutClearDataAction());
    }

    private getPrePopulatedMFAPhoneNumber(): string {
        return this.user?.phone
            ? AuthenticatedUserService.transformPrePopulatedMFAPhoneNumber(this.user.phone, this.currentOrg.country)
            : (this.currentOrg.country === 'US') ? '+1' : '+';
    }

    onSave() {
        this.sendUserVerificationCode();
    }

    private sendUserVerificationCode(): void {
        const digitsOnly = getDigitsOnly(this.phoneNumberCtrl.value);
        this.userService.sendUserVerificationCode(digitsOnly).pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                const user: User = copyData(this.user);
                user.mfaEnabled = true;
                user.mfaPhone = digitsOnly;

                const dialogConfig = new MatDialogConfig();
                dialogConfig.width = '570px';
                dialogConfig.data = {
                    user: user,
                    orgCountry: this.currentOrg.country,
                    view: SmsMfaDialogViews.VerifyPhone,
                    isLoginMFASetup: true
                };
                dialogConfig.disableClose = true;

                const dialogRef = this.dialog.open(VerifyPhoneDialogComponent, dialogConfig);

                dialogRef.afterClosed().pipe(takeUntil(this.unsubscribe$))
                    .subscribe(result => {
                        if (result) {
                            // credentials login once more & after login - turn on MFA login
                            this.performCredentialsLogin.emit();
                            this.turnOnCredentialsLoginView.emit();
                        }
                    });
            },
            err => {
                const errorMessage = (err.status === SEND_SMS_FAILED_STATUS_CODE
                    ? err.message
                    : MESSAGES.UNKNOWN_ERROR
                );
                this.snackBar.open(errorMessage, 'ERROR', <MatSnackBarConfig>{duration: 2000});
            });
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
